import { Component } from '@angular/core'
import {
  SummaryDataTableComponent,
  SummaryDataTableData,
  SummaryDataTableOptions
} from '../../common/summary-data-table/summary-data-table.component'
import {
  ANNOUNCEMENT_SUB_CATEGORY,
  ANNOUNCEMENT_ENTRY_ID,
  ANNOUNCEMENT_FROM,
  ANNOUNCEMENT_HEADING,
  ANNOUNCEMENT_TO,
  ANNOUNCEMENT_TYPE
} from 'src/app/constants/ui.columns'
import { Router } from '@angular/router'
import { AppState } from 'src/app/store/state/app.state'
import { Store } from '@ngrx/store'
import { KnowledgeService } from 'src/app/services/knowledge/knowledge.service'
import { Observable } from 'rxjs'
import { AnnouncementAPIModel, PaginatedAPI } from 'src/app/models/api.model'
import { getAllAnnouncementSummary } from 'src/app/store/selector/announcement.selector'
import { load_announcement_summary_action } from 'src/app/store/action/announcement.action'
import { GlobalConstants } from 'src/app/constants/global-constants'
import { ServiceUrlConstants } from 'src/app/constants/service-url-constants'
import { DocumentService } from 'src/app/services/common/document.service'
import { generateOptionsMapFromSet, selectCategoryType } from 'src/app/services/common/common.service'
import { getTaskCategorySubCategoryMapFromState } from 'src/app/store/selector/initialize.selector'

@Component({
  selector: 'app-announcement-summary',
  templateUrl: './announcement-summary.component.html',
  styleUrls: ['./announcement-summary.component.scss']
})
export class AnnouncementSummaryComponent {
  summaryOptions: SummaryDataTableOptions
  announcementList$: Observable<PaginatedAPI<AnnouncementAPIModel>>
  summaryData: SummaryDataTableData<AnnouncementAPIModel>

  queryParamValues = {
    pageNo: 0,
    pageLimit: SummaryDataTableComponent.defaultLimitDropdownValue,
    sortBy: 'heading',
    sortOrder: 'ASC'
  }

  searchRequestBody: any = {
    age: undefined
  }

  constructor(
    private route: Router,
    private appState: Store<AppState>,
    private knowledgeService: KnowledgeService,
    private documentService: DocumentService
  ) {
    this.summaryOptions = {
      downloadable: true,
      additionalButtons: true,
      contentSelectable: true,
      contentSelector: false,
      filterable: true,
      formattedContent: false,
      pageable: true,
      sortable: true,
      quickFilters: true
    }
  }
  ngOnInit(): void {
    this.announcementList$ = this.appState.select(getAllAnnouncementSummary())

    this.loadSummaryData()

    this.summaryData = {
      content$: this.announcementList$,
      headers: [
        ANNOUNCEMENT_SUB_CATEGORY,
        ANNOUNCEMENT_ENTRY_ID,
        ANNOUNCEMENT_TYPE,
        ANNOUNCEMENT_HEADING,
        ANNOUNCEMENT_FROM,
        ANNOUNCEMENT_TO
      ],
      additionalButtons: [
        {
          id: 'new-announcement',
          label: 'CREATE NEW',
          route: `/${GlobalConstants.ROUTING.ANNOUNCEMENT}/${GlobalConstants.ROUTING.NEW}`
        }
      ],
      currentSearchParams: this.queryParamValues,
      sortCallback: this.sortCallback,
      checkBoxSelectionIds: [],
      searchCallback: this.searchCallback,
      selectPageCallback: this.selectPageCallback,
      onDropdownLimitChange: this.onDropdownLimitChange,
      downloadDocumentCallback: this.downloadDocumentCallback
    }

    this.appState.select(getTaskCategorySubCategoryMapFromState('NA')).subscribe(categorySubCategoryMap => {
      if (categorySubCategoryMap) {
        const subCategoriesArr = Array.from(categorySubCategoryMap?.values()).flatMap(array => array)
        const uniqueSubCategoriesArr = new Set(subCategoriesArr)
        const taskSubCategoryMap = generateOptionsMapFromSet(uniqueSubCategoriesArr)
        this.addFiltersWithDynamicOptions(taskSubCategoryMap)
      }
    })
  }

  searchCallback = searchParams => {
    if (searchParams?.type) {
      searchParams.type = selectCategoryType(searchParams.type)
    }
    searchParams = this.filterEmptyProperties(searchParams)
    if (!Object.values(searchParams).some(value => value !== undefined && value !== '')) {
      searchParams = {}
    }
    this.queryParamValues.pageNo = 0
    this.searchRequestBody = {
      ...searchParams
    }
    this.loadSummaryData()
  }

  filterEmptyProperties(obj: any): any {
    const filteredObj: any = {}
    for (const key in obj) {
      if (obj.hasOwnProperty(key) && obj[key] !== '') {
        filteredObj[key] = obj[key]
      }
    }
    return filteredObj
  }
  onViewForChange = $event => {
    if ($event.target.value != 'undefined') {
      this.queryParamValues.pageLimit = $event.target.value.toString()
      const searchRequestBody = { ...this.searchRequestBody, age: $event.target.value }
      this.searchRequestBody = searchRequestBody
    } else {
      const searchRequestBody = { ...this.searchRequestBody, age: undefined }
      this.searchRequestBody = searchRequestBody
    }
    this.loadSummaryData()
  }

  sortCallback = (columnCode, order) => {
    this.queryParamValues.sortBy = columnCode
    this.queryParamValues.sortOrder = order
    this.loadSummaryData()
  }

  private loadSummaryData() {
    let options = {
      queryParams: this.buildQueryParams(),
      searchRequestBody: this.searchRequestBody
    }
    this.appState.dispatch(load_announcement_summary_action(options))
  }

  buildQueryParams = (buildForDownload = false): { key: string; value: string }[] => {
    return [
      {
        key: 'pageNo',
        value: buildForDownload
          ? GlobalConstants.FILE_DOWNLOAD_CONFIG.PAGE_NUMBER.toString()
          : this.queryParamValues.pageNo.toString()
      },
      {
        key: 'limit',
        value: buildForDownload
          ? GlobalConstants.FILE_DOWNLOAD_CONFIG.ROW_LIMIT.toString()
          : this.queryParamValues.pageLimit.toString()
      },
      {
        key: 'sortBy',
        value: this.queryParamValues.sortBy
      },
      {
        key: 'sortOrder',
        value: this.queryParamValues.sortOrder
      }
    ]
  }

  selectPageCallback = pageNum => {
    this.searchRequestBody = {}
    this.queryParamValues.pageNo = pageNum
    this.loadSummaryData()
  }

  private onDropdownLimitChange = (limit: number) => {
    this.queryParamValues.pageLimit = limit
    this.loadSummaryData()
  }

  downloadDocumentCallback = documentType => {
    const checkBoxSelectionIds = this.summaryData.checkBoxSelectionIds
    if (checkBoxSelectionIds.length > 0) {
      this.searchRequestBody = {
        ids: checkBoxSelectionIds
      }
    }
    const endPoint = `${ServiceUrlConstants.API_ANNOUNCEMENT_SUMMARY_LIST_EXPORT + documentType}`
    const options = {
      queryParams: this.buildQueryParams(true),
      requestBody: this.searchRequestBody
    }
    this.documentService.downloadDocument(endPoint, options)
  }

  addFiltersWithDynamicOptions(taskSubCategoryMap: Map<string, string>) {
    // Task Category
    ANNOUNCEMENT_SUB_CATEGORY.filter.getOptionsList = () => {
      return taskSubCategoryMap
    }
  }
}
